import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useApiClient } from "../../../api";
import { QuestionMappingForm } from "./QuestionMappingForm";
import PdfViewer from "./PdfViewer";

export const AssignmentUploadReviewDialog = ({
  open,
  setOpen,
  jobResult,
  assignment,
  onReviewed,
}) => {
  const ApiClient = useApiClient();
  const apiClientRef = useRef(ApiClient);
  apiClientRef.current = ApiClient;
  const [submitting, setSubmitting] = useState(false);
  const [sourcePageCount, setSourcePageCount] = useState(null);
  const [sourceFileUrl, setSourceFileUrl] = useState(null);
  const [loadingReview, setLoadingReview] = useState(false);

  useEffect(() => {
    if (!open || !jobResult?.id) return;
    let active = true;
    setLoadingReview(true);
    setSourceFileUrl(null);
    apiClientRef.current.assignment
      .getAssignmentUploadMappingReview(jobResult.id)
      .then((result) => {
        if (active) {
          setSourcePageCount(result?.sourcePageCount || 0);
          setSourceFileUrl(result?.sourceFileUrl || null);
        }
      })
      .catch(() => {
        if (active) setSourcePageCount(0);
      })
      .finally(() => {
        if (active) setLoadingReview(false);
      });
    return () => {
      active = false;
    };
  }, [jobResult?.id, open]);

  if (!jobResult) return null;

  const handleComplete = async () => {
    await onReviewed?.(jobResult.id);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && setOpen(false)}
      fullWidth
      maxWidth="xl"
      slotProps={{
        paper: {
          sx: {
            height: "90vh",
            backgroundImage: "none",
          },
        },
      }}
    >
      <DialogTitle>Review question page mappings</DialogTitle>
      <DialogContent sx={{ minHeight: 0 }}>
        {loadingReview ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ height: "100%" }}>
            <Grid size={{ xs: 12, md: sourceFileUrl ? 6 : 12 }}>
              {sourcePageCount === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Source document pages could not be loaded. Existing mappings
                  can still be reviewed, but page selections are unavailable.
                </Alert>
              )}
              <Box sx={{ height: "100%", overflowY: "auto", pr: 1 }}>
                <QuestionMappingForm
                  assignment={assignment}
                  jobResult={jobResult}
                  sourcePageCount={sourcePageCount}
                  onSubmittingChange={setSubmitting}
                  onComplete={handleComplete}
                />
              </Box>
            </Grid>
            {sourceFileUrl && (
              <Grid size={{ xs: 12, md: 6 }} sx={{ minHeight: 0 }}>
                <Box sx={{ height: "100%", overflow: "hidden" }}>
                  <PdfViewer pdfUrl={sourceFileUrl} />
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="question-page-mapping-form"
          variant="contained"
          disabled={submitting}
        >
          Save and reprocess
        </Button>
      </DialogActions>
    </Dialog>
  );
};
